import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CancellationMessage } from "~/components/assistant/messages/CancellationMessage";
import { DataValueMessage } from "~/components/assistant/messages/DataValueMessage";
import { DataframeMessage } from "~/components/assistant/messages/DataframeMessage";
import { FigureMessage } from "~/components/assistant/messages/FigureMessage";
import { ToolMessage } from "~/components/assistant/messages/ToolMessage";
import type { MessagePart } from "~/components/assistant/types";

// Shared markdown components for rendering markdown content
const MARKDOWN_COMPONENTS = {
  p: ({ children, ...props }: any) => (
    <p {...props} className="mb-2 last:mb-0">
      {children}
    </p>
  ),
  h1: ({ children, ...props }: any) => (
    <h1 {...props} className="text-lg font-semibold mb-2">
      {children}
    </h1>
  ),
  h2: ({ children, ...props }: any) => (
    <h2 {...props} className="text-base font-semibold mb-2">
      {children}
    </h2>
  ),
  h3: ({ children, ...props }: any) => (
    <h3 {...props} className="text-sm font-semibold mb-2">
      {children}
    </h3>
  ),
  ul: ({ children, ...props }: any) => (
    <ul {...props} className="list-disc list-inside mb-2 space-y-1">
      {children}
    </ul>
  ),
  ol: ({ children, ...props }: any) => (
    <ol {...props} className="list-decimal list-inside mb-2 space-y-1">
      {children}
    </ol>
  ),
  li: ({ children, ...props }: any) => (
    <li {...props} className="ml-2">
      {children}
    </li>
  ),
  code: ({ inline, children, ...props }: any) => {
    if (inline) {
      return (
        <code
          {...props}
          className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm font-mono"
        >
          {children}
        </code>
      );
    }
    return (
      <pre className="bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-x-auto mb-2">
        <code {...props} className="text-sm font-mono">
          {children}
        </code>
      </pre>
    );
  },
  a: ({ children, ...props }: any) => (
    <a
      {...props}
      className="text-blue-600 hover:underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  blockquote: ({ children, ...props }: any) => (
    <blockquote {...props} className="border-l-4 border-gray-300 pl-4 italic my-2">
      {children}
    </blockquote>
  ),
  table: ({ children, ...props }: any) => (
    <div className="overflow-x-auto my-2">
      <table {...props} className="min-w-full divide-y divide-gray-300">
        {children}
      </table>
    </div>
  ),
  thead: ({ children, ...props }: any) => (
    <thead {...props} className="bg-gray-50 dark:bg-gray-800">
      {children}
    </thead>
  ),
  tbody: ({ children, ...props }: any) => (
    <tbody {...props} className="divide-y divide-gray-200 dark:divide-gray-700">
      {children}
    </tbody>
  ),
  tr: ({ children, ...props }: any) => <tr {...props}>{children}</tr>,
  th: ({ children, ...props }: any) => (
    <th {...props} className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider">
      {children}
    </th>
  ),
  td: ({ children, ...props }: any) => (
    <td {...props} className="px-3 py-2 text-sm">
      {children}
    </td>
  ),
};

// Markdown component wrapper
function Markdown({ children }: { children: string }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
      {children}
    </ReactMarkdown>
  );
}

// Default renderer components
function ContentRenderer({ part }: { part: MessagePart; debugMode?: boolean }) {
  if (!part.content) {
    return null;
  }

  return (
    <div className="px-3 py-1">
      <Markdown>{part.content}</Markdown>
    </div>
  );
}

function CancelledRenderer() {
  return (
    <div className="px-3 py-1">
      <CancellationMessage />
    </div>
  );
}

function FigureRenderer({ part, debugMode }: { part: MessagePart; debugMode?: boolean }) {
  if (!part.figure) {
    return null;
  }
  return (
    <div className="px-2">
      <FigureMessage figure={part.figure} debugMode={debugMode || false} />
    </div>
  );
}

function DataframeRenderer({ part, debugMode }: { part: MessagePart; debugMode?: boolean }) {
  if (!part.dataframe) {
    return null;
  }
  return (
    <div className="px-2">
      <DataframeMessage dataframe={part.dataframe} debugMode={debugMode || false} />
    </div>
  );
}

function ValueRenderer({ part, debugMode }: { part: MessagePart; debugMode?: boolean }) {
  if (!part.value) {
    return null;
  }
  return (
    <div className="px-2">
      <DataValueMessage value={part.value} debugMode={debugMode || false} />
    </div>
  );
}

function ToolCallRenderer({ part, debugMode }: { part: MessagePart; debugMode?: boolean }) {
  if (!part.toolCall) {
    return null;
  }

  return (
    <div className="px-2">
      <ToolMessage toolCall={part.toolCall} debugMode={debugMode || false} expanded={false} />
    </div>
  );
}

// Export default renderers for registration
export const defaultMessageTypeRenderers = [
  {
    type: "content",
    component: ContentRenderer,
  },
  {
    type: "figure",
    component: FigureRenderer,
  },
  {
    type: "dataframe",
    component: DataframeRenderer,
  },
  {
    type: "value",
    component: ValueRenderer,
  },
  {
    type: "toolCall",
    component: ToolCallRenderer,
  },
  {
    type: "cancelled",
    component: CancelledRenderer,
  },
];
